import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders, HttpErrorResponse, HttpParams } from "@angular/common/http";
import { tap, catchError, retryWhen } from "rxjs/internal/operators";
import { throwError, from, Observable } from "rxjs";
import { SessionService } from "./session.service";
import { NetworkService, ConnectionStatus } from "./network.service";
import { NavController, ToastController } from "@ionic/angular";
import { FirebaseService } from "src/app/services/firebase.service";
import { AlertController } from "@ionic/angular";

import * as moment from "moment";
import { Order } from "../models/order.model";
import { User } from "../models/user.model";
import { Globals } from "../globals";
import { IApiEnvironment } from "../models/api-environment.model";
import { environment } from "src/environments/environment";
import { SearchParams } from "../pages/track/track.page";

@Injectable({
  providedIn: "root",
})
export class ApiService {
  UNAUTHENTICATED_ENVIRONMENT = "staging"; // TODO

  private readonly _allEnvironments = environment.environments;

  constructor(
    private http: HttpClient,
    private session: SessionService,
    private toastController: ToastController,
    private navController: NavController,
    private globals: Globals,
    public alertController: AlertController,
  ) {}

  /**
   * @returns URL based on environment parameter
   */
  baseUrl() {
    let baseUrl = this.session.getBaseUrl();
    return baseUrl;
  }

  private getLoginUrl(environmentName: string): string {
    let currentEnvironment: IApiEnvironment | undefined = this._allEnvironments.find((envObj) => {
      if (envObj.value?.toLowerCase() == environmentName?.toLowerCase()) {
        return true;
      } else {
        return false;
      }
    });

    const loginUrl = currentEnvironment?.endpoints?.auth ?? "";
    return loginUrl;
  }

  login(username, password, environment) {
    return this.http.post(`${this.getLoginUrl(environment)}`, `grant_type=password&username=${encodeURIComponent(username)}&password=${encodeURIComponent(password)}`, this.httpOptionsForLogin).pipe(
      tap(
        (data) => {
          //checking for scope and version matching
          if (this.globals.IS_BUSINESS_VERSION && data["scopes"].split(",").indexOf(this.globals.BUSINESS_SCOPE) === -1) {
            (<any>window).alert("Your account does not have access to this site. Please try again, or contact Carlile helpdesk for assistance.");
          } else if (!this.globals.IS_BUSINESS_VERSION && data["scopes"].split(",").indexOf(this.globals.CUSTOMER_SCOPE) === -1) {
            (<any>window).alert("Your account does not have access to this site. Please try again, or contact Carlile helpdesk for assistance.");
          } //
          else {
            console.warn("DATA: ", data);
            this.session.saveCredentials(data["access_token"], data[".expires"], data["refresh_token"], data["scopes"].split(","), username, environment);
          }

          let matchingEnvironment = this._allEnvironments.find((envObj) => {
            return envObj.value == environment;
          });
          console.warn("MATCHING ENVIRONMENT:", matchingEnvironment);
          this.session.saveBaseUrl(matchingEnvironment?.endpoints?.api);
        },
        (error) => console.log("ERROR", error),
      ),
    );
  }

  logout() {
    return from(this.session.clearSession());
    // TODO make API call to invalidate tokens server-side too
  }

  request(method: string, url: string, data: any) {
    return this.http.request(method, url, { body: data, headers: this.getAuthHeaders() });
  }

  rate(order) {
    return this.http.request("POST", `${this.baseUrl()}/api/quote/b2c`, { body: order, headers: this.getAuthHeaders() });
  }

  /**
   * gets profile information of currently logged in user
   */
  getCurrentUser(): Observable<User> {
    return this.http.get(`${this.baseUrl()}/api/users/me`, this.httpOptions).pipe(
      tap({
        next: (res: User) => {
          this.session.setCurrentStoredUser(res);
        },
        error: (error) => {
          console.warn("Error getting current user. Session Service will not be updated.", error);
          this.navController.navigateRoot(["/login"]);
          this.toastController
            .create({
              message: `Something went wrong fetching your account details. Please try again later.`,
              duration: 3000,
              color: "danger",
              position: "bottom",
            })
            .then((toast) => toast?.present());
        },
      }),
    );
  }

  /**
   * updates currently-logged in user's information
   */
  putCurrentUser(user: User) {
    return this.http.put(`${this.baseUrl()}/api/users/me`, user, this.httpOptions).pipe(
      tap({
        next: (res: User) => {
          this.session.setCurrentStoredUser(res);
        },
        error: (error) => {
          console.warn("Error getting current user. Session Service will not be updated.", error);
        },
      }),
    );
  }

  validateDiscount(discountCode) {
    return this.http.get(`${this.baseUrl()}/api/discountcodes/${discountCode}/validate`, this.httpOptions);
  }
  getEmail(email) {
    return this.http.get(`${this.baseUrl()}/api/users/exists?emailAddress=${email}`, this.httpOptions);
  }

  postUser(user: User) {
    return this.http.post(`${this.baseUrl()}/api/users`, user);
  }

  postNewPassword(email) {
    return this.http.post(`${this.baseUrl()}/api/users/password/reset`, email, this.httpOptions);
  }

  getInvoices(fromDate, toDate, startRow) {
    // return this.http.get('../../assets/data/invoices.mock.json', this.httpOptions);
    return this.http.get(`${this.baseUrl()}/api/invoices/grid?fromDate=${fromDate}&toDate=${toDate}&startRow=${startRow}`, this.httpOptions);
  }

  getLocations() {
    return this.http.get(`${this.baseUrl()}/api/locations/`);
  }

  getDiscountCodes() {
    return this.http.get(`${this.baseUrl()}/api/discountcodes/`, this.httpOptions);
  }
  postDiscountCode(code) {
    return this.http.post(`${this.baseUrl()}/api/discountcodes/`, code, this.httpOptions);
  }
  editDiscountCode(code, codeId: number) {
    return this.http.put(`${this.baseUrl()}/api/discountcodes/${codeId}`, code, this.httpOptions);
  }
  deleteDiscountCode(codeId) {
    return this.http.delete(`${this.baseUrl()}/api/discountcodes/${codeId}`, this.httpOptions);
  }

  getCurrentOrders() {
    const url = `${this.baseUrl()}/api/orders/consumer/current/`;
    return this.http.get(url, this.httpOptions);
  }

  getCurrentOpenOrdersBusiness(pageNumber, sortBy?: string, searchParams?: SearchParams) {
    let url = `${this.baseUrl()}/api/invoices/open`;
    let params = new HttpParams().set("page", pageNumber).set("pageSize", 20);

    if (sortBy) {
      params = params.set("sortBy", sortBy);
    }

    if (searchParams) {
      if (searchParams.accountNumber) {
        params = params.set("accountNumber", searchParams.accountNumber);
      }

      if (searchParams.invoiceNumber) {
        params = params.set("invoiceNumber", searchParams.invoiceNumber);
      }

      if (searchParams.invoiceDate) {
        params = params.set("fromDate", searchParams.invoiceDate.split("T")[0]);
        params = params.set("toDate", searchParams.invoiceDate.split("T")[0]);
      }
    }

    console.log("Getting invoices with the following params: ", params);
    return this.http.get(url, { ...this.httpOptions, params: params });
  }

  getCurrentPaidOrdersBusiness(pageNumber) {
    return this.http.get(`${this.baseUrl()}/api/invoices/paid?page=${pageNumber}&pageSize=250`, this.httpOptions);
  }

  patchOrder(orderNumber: string, order) {
    return this.http.patch(`${this.baseUrl()}/api/orders/${orderNumber}`, order, this.httpOptions);
  }

  patchPassword(hashPassword) {
    return this.http.patch(`${this.baseUrl()}/api/users/password`, hashPassword, this.httpOptions);
  }

  postOrder(order) {
    return this.http.post(`${this.baseUrl()}/api/orders/`, order, this.httpOptions);
  }

  // deprecated world-pay code
  postPayment(paymentPayload) {
    return this.http.post(`${this.baseUrl()}/api/payment/`, paymentPayload, this.httpOptions);
  }

  // deprecated Stripe checkout code
  postStripePayment(paymentPayload) {
    return this.http.post(`${this.baseUrl()}/api/payment/stripe`, paymentPayload, this.httpOptions);
  }

  // new UTA payment code
  postUtaToken() {
    return this.http.post(`${this.baseUrl()}/api/uta/newUtaToken`, null, this.httpOptions);
  }

  // new UTA payment code
  postUtaPayment(paymentPayload) {
    console.warn("Posting UTA payment with payload: ", paymentPayload);
    return this.http.post(`${this.baseUrl()}/api/payments`, paymentPayload, this.httpOptions);
  }

  postUtaPaymentWithSurcharge(paymentId: string, ccToken: string) {
    console.warn("Posting UTA Payment with Surcharge Calculation: ", {
      paymentId: paymentId,
      ccToken: ccToken,
    });
    const url = `${this.baseUrl()}/api/uta/payments/${paymentId}/surcharge`;
    const requestBody = { token: ccToken };
    return this.http.post(url, requestBody, this.httpOptions);
  }

  // complete a payment via API once UTA has tokenized a card
  postUtaPaymentComplete(paymentId: string, requestBody): Observable<Object> {
    console.warn(`Completing payment ${paymentId} with request body: `, requestBody);
    return this.http.post(`${this.baseUrl()}/api/uta/payments/${paymentId}/complete`, requestBody, this.httpOptions);
  }

  getSavedCard() {
    return this.http.get(`${this.baseUrl()}/api/uta/savedCard`, this.httpOptions);
  }

  deleteSavedCard() {
    return this.http.delete(`${this.baseUrl()}/api/uta/savedCard`, this.httpOptions);
  }

  extractErrors(response) {
    return response["error"]["errors"].map((error) => error["errorMessage"]);
  }

  private get httpOptions() {
    return {
      headers: this.getAuthHeaders(),
    };
  }

  getAuthHeaders() {
    return new HttpHeaders({
      Authorization: `Bearer ${this.session.accessToken}`,
    });
  }

  private get httpOptionsForLogin() {
    return {
      headers: new HttpHeaders({
        "Content-Type": "application/x-www-form-urlencoded",
      }),
    };
  }

  private handleError(error: HttpErrorResponse) {
    if (error.error instanceof ErrorEvent) {
      // A client-side or network error occurred. Handle it accordingly.
      console.error("An error occurred:", error.error.message);
    } else {
      // Back-end error
      console.error(`HTTP error ${error.status}, body: ${error.error}`);

      if (error.status == 401) {
        return throwError("Invalid username or password. Please try again, or contact Carlile helpdesk for assistance.");
      } else {
        return throwError(`An error occurred while logging in. Please try again, or contact Carlile helpdesk for assistance.\n\n[Error code: ${error.status} ${error.statusText}]`);
      }
    }
  }

  async presentAlert() {
    const alert = await this.alertController.create({
      header: "Alert",
      subHeader: "Subtitle",
      message: "This is an alert message.",
      buttons: ["OK"],
    });

    await alert.present();
  }
}
